import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import type { CookingHistoryEntry } from '@/types';
import {
  getCookingHistory,
  addCookingHistoryEntry,
  clearCookingHistory,
} from '@/lib/data-layer';
import { generateId } from '@/lib/utils';

interface HistoryState {
  // Data
  cookingHistory: CookingHistoryEntry[];
  isLoading: boolean;
  error: string | null;

  // Actions
  loadHistory: () => Promise<void>;
  addHistoryEntry: (entry: Omit<CookingHistoryEntry, 'id' | 'cookedAt'>) => Promise<void>;
  clearHistory: () => Promise<void>;

  // Computed
  getRecentHistory: (limit?: number) => CookingHistoryEntry[];
  getHistoryByRecipe: (recipeId: string) => CookingHistoryEntry[];
}

export const useHistoryStore = create<HistoryState>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Initial state
      cookingHistory: [],
      isLoading: false,
      error: null,

      // Load cooking history from IndexedDB
      loadHistory: async () => {
        set({ isLoading: true, error: null });
        try {
          const history = await getCookingHistory();
          set({ cookingHistory: history, isLoading: false });
        } catch (error) {
          console.error('Failed to load cooking history:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to load history',
            isLoading: false,
          });
        }
      },

      // Add a new history entry
      addHistoryEntry: async (entryData) => {
        const entry: CookingHistoryEntry = {
          ...entryData,
          id: generateId(),
          cookedAt: new Date().toISOString(),
        };

        try {
          await addCookingHistoryEntry(entry);
          set((state) => ({
            cookingHistory: [entry, ...state.cookingHistory],
          }));
        } catch (error) {
          console.error('Failed to add history entry:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to add history entry',
          });
        }
      },

      // Clear all cooking history
      clearHistory: async () => {
        try {
          await clearCookingHistory();
          set({ cookingHistory: [] });
        } catch (error) {
          console.error('Failed to clear cooking history:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to clear history',
          });
        }
      },

      // Get recent history entries
      getRecentHistory: (limit = 10) => {
        return get().cookingHistory.slice(0, limit);
      },

      // Get history for a specific recipe
      getHistoryByRecipe: (recipeId: string) => {
        return get().cookingHistory.filter((entry) => entry.recipeId === recipeId);
      },
    })),
    { name: 'history-store' }
  )
);
